<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import { getDataProtectionPermalink } from '~/lib'
import { useI18n } from 'vue-i18n'
import type { Customer } from '~/types/shopware'

type ClientApiError = { messages: { detail: string }[] }
type CustomerAccountDetails = Customer & {
  company?: string
  vatIds?: string[]
  customFields?: {
    custom_customer_info_marketing_material?: boolean
  }
}
type UpdatePersonalInfoPayload = Parameters<ReturnType<typeof useUser>['updatePersonalInfo']>[0]

const { user, refreshUser, updatePersonalInfo, updateEmail } = useUser()
const { pushError, pushSuccess } = useNotifications()
const { t } = useI18n()
const localePath = useLocalePath()

const loadingData = ref(false)

const state = ref({
  firstName: '',
  lastName: '',
  salutationId: '',
  title: '',
  accountType: '',
  company: '',
  vat: '',
  email: {
    email: '',
    email_confirm: '',
    password: '',
  },
  marketingConsent: false,
})

const isEmailChanging = computed(() => state.value.email.email !== user.value?.email)

const invokeUpdate = async (): Promise<void> => {
  try {
    loadingData.value = true

    await updatePersonalInfo({
      firstName: state.value.firstName,
      lastName: state.value.lastName,
      salutationId: state.value.salutationId,
      title: state.value.title,
      accountType: state.value.accountType,
      company: state.value.company,
      vatIds: state.value.vat ? [state.value.vat] : [],
      customFields: {
        custom_customer_info_marketing_material: state.value.marketingConsent,
      },
    } as UpdatePersonalInfoPayload)

    if (isEmailChanging.value) {
      await updateEmail({
        email: state.value.email.email,
        emailConfirmation: state.value.email.email_confirm,
        password: state.value.email.password,
      })
    }

    pushSuccess(t('shop.accountPage.profilePage.personalDetailsPage.updateSuccessMessage'))

    refreshUser()
  } catch (err) {
    const e = err as ClientApiError
    e.messages.forEach((m) => pushError(m.detail))
  } finally {
    loadingData.value = false
  }
}
onBeforeMount(async () => {
  loadingData.value = true
  await refreshUser()
  const currentUser = user.value as CustomerAccountDetails | undefined
  state.value.accountType = currentUser?.accountType || ''
  state.value.firstName = user.value?.firstName || ''
  state.value.lastName = user.value?.lastName || ''
  state.value.email.email = user.value?.email || ''
  state.value.salutationId = user.value?.salutationId || ''
  state.value.title = user.value?.title || ''
  state.value.company = currentUser?.company || ''
  state.value.vat = currentUser?.vatIds?.[0] || ''
  state.value.marketingConsent = currentUser?.customFields?.custom_customer_info_marketing_material || false
  loadingData.value = false
})
</script>
<template>
<FormKitLazyProvider config-file="true">

  <!-- TODO: Fix select not taking default value into account -->
  <p>{{ t('shop.accountPage.profilePage.personalDetailsPage.description') }}</p>
  <ShopNotifications />
  <FormKit v-model="state" type="form" :actions="false" :incomplete-message="false" @submit="invokeUpdate">
    <div class="wrapper two-columns">
      <Select
        id="account-details-accountType"
        name="accountType"
        :required="true"
        :disabled="loadingData"
        :label="`${t('shop.registrationPage.registrationForm.accountType')} *`"
        :options="[
          { value: 'private', label: t('shop.registrationPage.registrationForm.accountTypes.private') },
          { value: 'business', label: t('shop.registrationPage.registrationForm.accountTypes.business') },
        ]"
      />
      <Input
        id="account-details-title"
        name="title"
        :required="false"
        :disabled="loadingData"
        :label="t('shop.registrationPage.registrationForm.title')"
      />
      <Input
        id="account-details-firstName"
        name="firstName"
        :required="true"
        :disabled="loadingData"
        :label="`${t('shop.registrationPage.registrationForm.firstName')} *`"
      />
      <Input
        id="account-details-lastName"
        name="lastName"
        :required="true"
        :disabled="loadingData"
        :label="`${t('shop.registrationPage.registrationForm.lastName')} *`"
      />
      <Input
        v-if="state.accountType === 'business'"
        id="account-details-company"
        name="company"
        :required="true"
        :disabled="loadingData"
        :label="`${t('shop.registrationPage.registrationForm.company')} *`"
      />
      <Input
        v-if="state.accountType === 'business'"
        id="account-details-vat"
        name="vat"
        :required="false"
        :disabled="loadingData"
        :label="t('shop.registrationPage.registrationForm.vat')"
      />
    </div>
    <FormKit type="group" name="email">
      <div class="wrapper">
        <Email
          id="account-details-email"
          name="email"
          :required="true"
          :disabled="loadingData"
          :label="`${t('shop.registrationPage.registrationForm.email')} *`"
        />
      </div>
      <div v-if="isEmailChanging" class="wrapper two-columns">
        <Email
          id="account-details-email_confirm"
          name="email_confirm"
          :required="true"
          :disabled="loadingData"
          validation="confirm"
          :label="`${t('shop.accountPage.profilePage.personalDetailsPage.newEmail')} *`"
        />
        <Password
          id="account-details-password"
          name="password"
          :required="true"
          :disabled="loadingData"
          :label="`${t('shop.accountPage.profilePage.personalDetailsPage.password')} *`"
        />
      </div>
    </FormKit>
    <ShopMarketingConsentCheckbox v-model="state.marketingConsent" />
    <Button primary slide type="submit" :disabled="loadingData">
      {{ t('shop.accountPage.profilePage.personalDetailsPage.save') }}
    </Button>
  </FormKit>
  <p class="text-small">
    {{ t('shop.accountPage.profilePage.personalDetailsPage.privacyMessage') }}
    <NuxtLink :to="localePath(getDataProtectionPermalink())">
      {{ t('shop.accountPage.profilePage.personalDetailsPage.privacyPolicy') }}
    </NuxtLink>
  </p>

</FormKitLazyProvider>
</template>
<style scoped lang="scss">
.wrapper {
  display: grid;
  gap: var(--form-spacing);
  margin-bottom: 20px;
  &.two-columns {
    grid-template-columns: 1fr;
    @include breakpoint(ts) {
      grid-template-columns: 1fr 1fr;
    }
  }
}
.text-small {
  margin-top: 20px;
}
</style>
